import { useEffect } from 'react';

import { useEcosystem } from '@/shared/contexts/EcosystemContext';
import { getPlatformApexOrigin } from '@/shared/utils/ecosystemRouting';

/**
 * useRedirectToApex: for pages that must only ever render at the platform
 * apex (e.g. Dashboard), force a full navigation to the canonical apex URL
 * when the page is reached with a tenant resolved (subdomain or path mode).
 *
 * React Router's basename means a tenant-nested URL (e.g.
 * costem.rileconnect.dev/dashboard, or rileconnect.dev/costem/dashboard)
 * and the bare apex URL both resolve to the exact same route internally,
 * so this check has to happen inside the page itself using the resolved
 * ecosystem context, not at the route-definition level.
 *
 * @param {string} path - the canonical apex path, e.g. '/dashboard'.
 * @returns {boolean} true while a redirect is in flight, so the caller can
 *   render a redirecting state instead of its normal content.
 */
export default function useRedirectToApex(path) {
    const { isPlatform, loading } = useEcosystem();
    const shouldRedirect = !loading && !isPlatform;

    useEffect(() => {
        if (shouldRedirect) {
            window.location.href = `${getPlatformApexOrigin()}${path}`;
        }
    }, [shouldRedirect, path]);

    return shouldRedirect;
}
